import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import moment from 'moment';
import 'moment-timezone';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const Filters = ({ visible, meta, setMeta, carriers, tags, orderTags, report }) => {
    const profile = useSelector(state => state.profile.profile);
    const [filterButtonDisabled, setFilterButtonDisabled] = useState(true);
    const [clearButtonDisabled, setClearButtonDisabled] = useState(true); // --- ADDED: EXPLICIT CLEAR BUTTON STATE CONTROL TRACKER ---
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [switchStatus, setSwitchStatus] = useState(false);

    // Local Buffer State for Dropdowns
    const [localFilters, setLocalFilters] = useState({
        order_status: [],
        order_value: "all",
        shipment_type: 0,
        delivery_type: "all",
        platform_tags: [],
        order_tags: [],
        start_date: "",
        end_date: "",
        sc_generated: "false"
    });

    // Order Value Threshold Arrays
    const orderValues = [
        { "display_name": "All", "name": "all" },
        { "display_name": "Regular ($15 and Under)", "name": "under15" },
        { "name": "above15", "display_name": "Parcel (Above $15)" }
    ];
    const orderValuesNew = [
        { "display_name": "All", "name": "all" },
        { "display_name": "Regular (Under $20)", "name": "under20" },
        { "name": "between20_35", "display_name": "Parcel ($20 to $34.99)" },
        { "name": "above35", "display_name": "Large Parcel ($35+)" }
    ];
    const orderValuesNewUpdate = [
        { "display_name": "All", "name": "all" },
        { "display_name": "Under $800", "name": "under800" },
        { "display_name": "Above $800", "name": "above800" }
    ];
    const orderValues1599 = [
        { "display_name": "All", "name": "all" },
        { "display_name": "Regular (Under $15)", "name": "under15" },
        { "display_name": "Parcel ($15 to $36)", "name": "between15_36" },
        { "display_name": "CT ($36+)", "name": "CT36" }
    ];

    // Map incoming props cleanly into the local workspace on load
    useEffect(() => {
        if (meta) {
            setLocalFilters({
                order_status: meta.order_status || [],
                order_value: (meta.order_value === "" || !meta.order_value) ? "all" : meta.order_value,
                shipment_type: meta.shipment_type ?? 0,
                delivery_type: (meta.delivery_type === "" || !meta.delivery_type) ? "all" : meta.delivery_type,
                platform_tags: meta.platform_tags || [],
                order_tags: meta.order_tags || [],
                start_date: meta.start_date || "",
                end_date: meta.end_date || "",
                sc_generated: meta.sc_generated || "false"
            });

            if (meta.start_date) {
                setStartDate(moment.utc(meta.start_date).local().toDate());
            } else {
                setStartDate(null);
            }
            if (meta.end_date) {
                setEndDate(moment.utc(meta.end_date).local().toDate());
            } else {
                setEndDate(null);
            }
            setSwitchStatus(meta.sc_generated === "true" || meta.sc_generated === true);
        }
    }, [meta]);

    const onInputChange = (e) => {
        const { name, value } = e.target;
        setLocalFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const submitFilterValues = () => {
        setMeta(prevState => ({
            ...prevState,
            ...localFilters,
            [`delivery_type`]: localFilters.delivery_type === "all" ? "" : localFilters.delivery_type,
            [`order_value`]: localFilters.order_value === "all" ? "" : localFilters.order_value,
            [`page`]: 1
        }));
    };

    const onDateChange = (date_type, date) => {
        if (date !== "" && typeof date !== "undefined" && date != null) {
            let date_formatted = moment(date).format("YYYY-MM-DD");
            if (date_type === "start_date") {
                let start_date = date_formatted + " 00:00:00";
                let start_date_format = (profile && profile.time_zone)
                    ? moment.tz(start_date, profile.time_zone).utc().format("YYYY-MM-DD HH:mm:ss")
                    : moment(start_date).utc().format("YYYY-MM-DD HH:mm:ss");
                
                setStartDate(date);
                setLocalFilters(prev => ({ ...prev, [`start_date`]: start_date_format }));
            } else {
                let end_date = date_formatted + " 23:59:59";
                let end_date_format = (profile && profile.time_zone)
                    ? moment.tz(end_date, profile.time_zone).utc().format("YYYY-MM-DD HH:mm:ss")
                    : moment(end_date).utc().format("YYYY-MM-DD HH:mm:ss");
                
                setEndDate(date);
                setLocalFilters(prev => ({ ...prev, [`end_date`]: end_date_format }));
            }
        } else {
            if (date_type === "start_date") {
                setStartDate(null);
                setLocalFilters(prev => ({ ...prev, [`start_date`]: "" }));
            } else {
                setEndDate(null);
                setLocalFilters(prev => ({ ...prev, [`end_date`]: "" }));
            }
        }
    };

    const onChangeSwitch = (switchValue) => {
        setSwitchStatus(switchValue);
        setLocalFilters(prev => ({
            ...prev,
            [`sc_generated`]: switchValue
        }));
    };

    const handleKeyUp = (event) => {
        if (event.keyCode === 13) {
            submitFilterValues();
        }
    };

    const resetFields = () => {
        setStartDate(null);
        setEndDate(null);
        setSwitchStatus(false);
        
        let targetStatus = [];
        if (report === 'openorders') {
            targetStatus = ["unfulfilled"];
        } else if (report === 'returnorders') {
            targetStatus = ["return"];
        }

        const clearedFilters = {
            order_status: targetStatus,
            order_value: "all",
            shipment_type: 0,
            delivery_type: "all",
            start_date: "",
            end_date: "",
            sc_generated: "false",
            platform_tags: [],
            order_tags: []
        };
        
        setLocalFilters(clearedFilters);
        
        setMeta(prevState => ({
            ...prevState,
            ...clearedFilters,
            [`delivery_type`]: "",
            [`order_value`]: "",
            [`page`]: 1
        }));
    };

    const checkFormHasValues = () => {
        const isStatusChanged = JSON.stringify(localFilters.order_status) !== JSON.stringify(meta?.order_status || []);
        const isStartDateChanged = localFilters.start_date !== (meta?.start_date || "");
        const isEndDateChanged = localFilters.end_date !== (meta?.end_date || "");
        const isScGeneratedChanged = String(localFilters.sc_generated) !== String(meta?.sc_generated || "false");
        const isPlatformTagsChanged = JSON.stringify(localFilters.platform_tags) !== JSON.stringify(meta?.platform_tags || []);
        const isOrderTagsChanged = JSON.stringify(localFilters.order_tags) !== JSON.stringify(meta?.order_tags || []);
        const isShipmentTypeChanged = Number(localFilters.shipment_type) !== Number(meta?.shipment_type || 0);

        const normalizedPropDeliveryType = (meta?.delivery_type === "" || !meta?.delivery_type) ? "all" : meta.delivery_type;
        const isDeliveryTypeChanged = localFilters.delivery_type !== normalizedPropDeliveryType;

        const normalizedPropOrderValue = (meta?.order_value === "" || !meta?.order_value) ? "all" : meta.order_value;
        const isOrderValueChanged = localFilters.order_value !== normalizedPropOrderValue;

        if (
            isStatusChanged ||
            isStartDateChanged ||
            isEndDateChanged ||
            isScGeneratedChanged ||
            isPlatformTagsChanged ||
            isOrderTagsChanged ||
            isShipmentTypeChanged ||
            isDeliveryTypeChanged ||
            isOrderValueChanged
        ) {
            return false;
        }
        return true;
    };

    // --- ADDED: EVALUATES IF ACTIVE MODIFIERS EXIST TO ENGAGE/RELEASE CLEAR OPTION CONTROL ---
    const checkIsClearDisabled = () => {
        let baseStatus = [];
        if (report === 'openorders') {
            baseStatus = ["unfulfilled"];
        } else if (report === 'returnorders') {
            baseStatus = ["return"];
        }

        const isStatusAtDefault = JSON.stringify(localFilters.order_status) === JSON.stringify(baseStatus);
        const isStartDateEmpty = localFilters.start_date === "";
        const isEndDateEmpty = localFilters.end_date === "";
        const isScGeneratedAtDefault = String(localFilters.sc_generated) === "false";
        const isPlatformTagsEmpty = localFilters.platform_tags.length === 0;
        const isOrderTagsEmpty = localFilters.order_tags.length === 0;
        const isShipmentTypeAtDefault = Number(localFilters.shipment_type) === 0;
        const isDeliveryTypeAtDefault = localFilters.delivery_type === "all";
        const isOrderValueAtDefault = localFilters.order_value === "all";

        return (
            isStatusAtDefault &&
            isStartDateEmpty &&
            isEndDateEmpty &&
            isScGeneratedAtDefault &&
            isPlatformTagsEmpty &&
            isOrderTagsEmpty &&
            isShipmentTypeAtDefault &&
            isDeliveryTypeAtDefault &&
            isOrderValueAtDefault
        );
    };

    useEffect(() => {
        setFilterButtonDisabled(checkFormHasValues());
        setClearButtonDisabled(checkIsClearDisabled()); // Evaluates clear readiness state independently
    }, [localFilters, switchStatus, meta]);

    // --- FIXED: ROUTING LINK NAVIGATION RESET LOOP ---
    useEffect(() => {
        setStartDate(null);
        setEndDate(null);
        setSwitchStatus(false);

        let initialOrderStatus = [];
        if (report === 'openorders') {
            initialOrderStatus = ["unfulfilled"];
        } else if (report === 'returnorders') {
            initialOrderStatus = ["return"];
        }

        const freshBaseFilters = {
            order_status: initialOrderStatus,
            order_value: "all",
            shipment_type: 0,
            delivery_type: "all",
            start_date: "",
            end_date: "",
            sc_generated: "false",
            platform_tags: [],
            order_tags: []
        };

        // 1. Wipe local buffer values completely
        setLocalFilters(freshBaseFilters);

        // 2. Transmit the absolute reset configurations to parent state mapping
        setMeta(prevState => ({
            ...prevState,
            ...freshBaseFilters,
            [`delivery_type`]: "",
            [`order_value`]: "",
            [`page`]: 1
        }));

    }, [report]); // Monitors dynamic report parameters context directly

    const formFieldStyles = {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: '#FFFFFF',
            transition: 'all 0.2s ease-in-out',
            border: '1px solid #E2E8F0',
            '& fieldset': { border: 'none' },
            '&:hover': {
                borderColor: '#CBD5E1',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
            },
            '&.Mui-focused': {
                borderColor: '#1E293B',
                boxShadow: '0 0 0 3px rgba(30, 41, 59, 0.08)'
            }
        },
        '& .MuiInputLabel-outlined': {
            color: '#64748B',
            fontWeight: 500,
            transform: 'translate(14px, 10px) scale(1)',
            '&.Mui-focused, &.MuiInputLabel-shrink': {
                transform: 'translate(14px, -8px) scale(0.75)',
                color: '#1E293B',
                fontWeight: 600
            }
        },
        '& .MuiSelect-select, & input': {
            padding: '10px 14px',
            fontSize: '14px',
            color: '#1E293B',
            fontWeight: 500
        }
    };

    return (
        <>
            {visible && (
                <Box 
                    className="filtercontainer" 
                    sx={{ 
                        width: '100%',
                        bgcolor: '#F8FAFC',
                        borderRadius: '16px',
                        border: '1px solid #E2E8F0',
                        p: { xs: 2, md: 3 },
                        mb: 3,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                    }}
                >
                    <div className="filteraccordion" onKeyUp={handleKeyUp}>
                        <Grid container spacing={2.5} alignItems="center">
                            
                            {/* 1. Order Status */}
                            {report === "" && (
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <FormControl fullWidth variant="outlined" sx={formFieldStyles}>
                                        <InputLabel id="order-status-select-label">Order Status</InputLabel>
                                        <Select
                                            labelId="order-status-select-label"
                                            id="order-status-select"
                                            value={localFilters.order_status || []}
                                            name="order_status"
                                            onChange={onInputChange}
                                        >
                                            <MenuItem value="unfulfilled">Not Yet Fulfilled</MenuItem>
                                            <MenuItem value="fulfilled">Fulfilled</MenuItem>
                                            <MenuItem value="return">Return</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* 2. Order Values Selector Modules */}
                            {profile?.id === 1775 && (
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <FormControl fullWidth variant="outlined" sx={formFieldStyles}>
                                        <InputLabel id="order-value-1775-label">Order Value</InputLabel>
                                        <Select
                                            labelId="order-value-1775-label"
                                            id="order-value-1775"
                                            value={localFilters.order_value}
                                            name="order_value"
                                            onChange={onInputChange}
                                        >
                                            {orderValues.map((item) => (
                                                <MenuItem key={item.name} value={item.name}>{item.display_name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {profile?.id === 1599 && (
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <FormControl fullWidth variant="outlined" sx={formFieldStyles}>
                                        <InputLabel id="order-value-1599-label">Order Value</InputLabel>
                                        <Select
                                            labelId="order-value-1599-label"
                                            id="order-value-1599"
                                            value={localFilters.order_value}
                                            name="order_value"
                                            onChange={onInputChange}
                                        >
                                            {orderValues1599.map((item) => (
                                                <MenuItem key={item.name} value={item.name}>{item.display_name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {profile?.id === 1754 && (
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <FormControl fullWidth variant="outlined" sx={formFieldStyles}>
                                        <InputLabel id="order-value-1754-label">Order Value</InputLabel>
                                        <Select
                                            labelId="order-value-1754-label"
                                            id="order-value-1754"
                                            value={localFilters.order_value}
                                            name="order_value"
                                            onChange={onInputChange}
                                        >
                                            {orderValuesNew.map((item) => (
                                                <MenuItem key={item.name} value={item.name}>{item.display_name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {profile?.id === 1850 && (
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <FormControl fullWidth variant="outlined" sx={formFieldStyles}>
                                        <InputLabel id="order-value-1850-label">Order Value</InputLabel>
                                        <Select
                                            labelId="order-value-1850-label"
                                            id="order-value-1850"
                                            value={localFilters.order_value}
                                            name="order_value"
                                            onChange={onInputChange}
                                        >
                                            {orderValuesNewUpdate.map((item) => (
                                                <MenuItem key={item.name} value={item.name}>{item.display_name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* 3. Platform Tags */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth variant="outlined" sx={formFieldStyles}>
                                    <InputLabel id="platform-tags-select-label">Platform Tags</InputLabel>
                                    <Select
                                        multiple
                                        labelId="platform-tags-select-label"
                                        id="platform-tags-select"
                                        value={localFilters.platform_tags || []}
                                        name="platform_tags"
                                        MenuProps={{ PaperProps: { style: { maxHeight: 350, borderRadius: '8px' } } }}
                                        onChange={onInputChange}
                                    >
                                        {tags?.data?.map((item) => (
                                            <MenuItem key={item[0]} value={item[0]}>{item[0]}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* 4. Order Tags */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth variant="outlined" sx={formFieldStyles}>
                                    <InputLabel id="order-tags-select-label">Order Tags</InputLabel>
                                    <Select
                                        multiple
                                        labelId="order-tags-select-label"
                                        id="order-tags-select"
                                        value={localFilters.order_tags || []}
                                        name="order_tags"
                                        MenuProps={{ PaperProps: { style: { maxHeight: 350, borderRadius: '8px' } } }}
                                        onChange={onInputChange}
                                    >
                                        {orderTags?.data?.map((item) => (
                                            <MenuItem key={item.tag_name} value={item.tag_name}>{item.tag_name}</MenuItem>
                                        ))}
                                        <MenuItem value="NOTASSIGNED">Tag not assigned</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* 5. Types of Shipments */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth variant="outlined" sx={formFieldStyles}>
                                    <InputLabel id="shipment-type-select-label">Types of Shipments</InputLabel>
                                    <Select
                                        labelId="shipment-type-select-label"
                                        id="shipment-type-select"
                                        value={localFilters.shipment_type ?? 0}
                                        name="shipment_type"
                                        onChange={onInputChange}
                                    >
                                        <MenuItem value={0}>All Shipments</MenuItem>
                                        <MenuItem value={1}>Single Piece Shipments</MenuItem>
                                        <MenuItem value={2}>Multi Piece Shipments</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* 6. Delivery Type Dropdown Selector */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth variant="outlined" sx={formFieldStyles}>
                                    <InputLabel id="delivery-type-select-label">Delivery Type</InputLabel>
                                    <Select
                                        labelId="delivery-type-select-label"
                                        id="delivery-type-select"
                                        value={localFilters.delivery_type}
                                        name="delivery_type"
                                        onChange={onInputChange}
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="Shipping">Shipping</MenuItem>
                                        <MenuItem value="Store">Store</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* 7. Date Picker Range Container Block with Inline Clear Actions */}
                            <Grid item xs={12} md={6} lg={4}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
                                        <DatePicker
                                            label="Start Date"
                                            value={startDate}
                                            disableFuture={true}
                                            inputFormat="yyyy-MM-dd"
                                            onChange={(val) => onDateChange("start_date", val)}
                                            renderInput={(params) => (
                                                <TextField 
                                                    fullWidth 
                                                    size="small" 
                                                    variant="outlined" 
                                                    sx={formFieldStyles} 
                                                    {...params} 
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {startDate && (
                                                                    <IconButton 
                                                                        size="small" 
                                                                        onClick={() => onDateChange("start_date", null)}
                                                                        sx={{ marginRight: '-6px', color: '#94A3B8' }}
                                                                    >
                                                                        <ClearIcon fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                        <DatePicker
                                            label="End Date"
                                            value={endDate}
                                            disableFuture={true}
                                            inputFormat="yyyy-MM-dd"
                                            onChange={(val) => onDateChange("end_date", val)}
                                            renderInput={(params) => (
                                                <TextField 
                                                    fullWidth 
                                                    size="small" 
                                                    variant="outlined" 
                                                    sx={formFieldStyles} 
                                                    {...params} 
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {endDate && (
                                                                    <IconButton 
                                                                        size="small" 
                                                                        onClick={() => onDateChange("end_date", null)}
                                                                        sx={{ marginRight: '-6px', color: '#94A3B8' }}
                                                                    >
                                                                        <ClearIcon fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Stack>
                                </LocalizationProvider>
                            </Grid>

                            {/* 8. Show only SC Shipment Orders Switch Controller */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControlLabel
                                    sx={{ 
                                        userSelect: 'none',
                                        ml: 0.5,
                                        '& .MuiFormControlLabel-label': { 
                                            fontSize: '13.5px', 
                                            fontWeight: 600, 
                                            color: '#475569' 
                                        } 
                                    }}
                                    control={
                                        <Switch
                                            checked={switchStatus}
                                            onChange={(e) => onChangeSwitch(e.target.checked)}
                                            size="small"
                                            name="sc_generated" 
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#1976d2' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1976d2' }
                                            }}
                                        />
                                    }
                                    label="Show only SC shipment orders"
                                />
                            </Grid>

                        </Grid>

                        <Divider sx={{ my: 3, borderColor: '#E2E8F0', borderStyle: 'dashed' }} />

                        {/* Layout Footers - Primary CTA Action Triggers */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                            <Button 
                                variant="outlined" 
                                size="medium"
                                startIcon={<RestartAltIcon />}
                                onClick={resetFields} 
                                disabled={clearButtonDisabled} // --- MODIFIED: TARGETS NEW INDEPENDENT EVALUATOR STATE ---
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    px: 3,
                                    borderColor: '#E2E8F0',
                                    color: '#475569',
                                    '&:hover': {
                                        borderColor: '#1976d2',
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        color: '#1976d2'
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: '#E2E8F0',
                                        color: '#CBD5E1'
                                    }
                                }}
                            >
                                Clear
                            </Button>
                            <Button 
                                variant="contained" 
                                size="medium"
                                startIcon={<FilterAltIcon />}
                                onClick={submitFilterValues} 
                                disabled={filterButtonDisabled}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    px: 4,
                                    boxShadow: 'none',
                                    backgroundColor: '#1976d2', // Synced with corporate blue accent color theme
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#115293',
                                        boxShadow: 'none'
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: '#E2E8F0',
                                        color: '#94A3B8'
                                    }
                                }}
                            >
                                Filter
                            </Button>
                        </Box>
                    </div>
                </Box>
            )}
        </>
    );
};

export default Filters;